import { Icon, Option, Select } from "@quantumcast/ui";
import React from "react";
import { t } from "../../../../../global/Localization/translation";
import { Playlist } from "../../../domain";
import { PlaylistSequenceStep } from "../../../types";

import "./PlaylistSequence.scss";

type Props = {
  steps: PlaylistSequenceStep[];
  // Candidate drawers: every playlist in the library except sequences and self.
  playlists: Playlist[];
  onChange: (steps: PlaylistSequenceStep[]) => void;
};

const PlaylistSequence = (props: Props) => {
  const options: Option[] = props.playlists.map((p) => ({
    label: p.name,
    value: p.id,
  }));

  const labelFor = (id: string) =>
    props.playlists.find((p) => p.id === id)?.name ?? t("playlist.settings.sequence.deleted");

  const update = (next: PlaylistSequenceStep[]) => props.onChange(next);

  const onChangeStep = (index: number, _: string, option: Option) => {
    const next = [...props.steps];
    next[index] = { playlistId: option.value };
    update(next);
  };

  const onAdd = () => {
    const first = props.playlists[0];
    if (!first) return;
    update([...props.steps, { playlistId: first.id }]);
  };

  const onDelete = (index: number) => {
    const next = [...props.steps];
    next.splice(index, 1);
    update(next);
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= props.steps.length) return;
    const next = [...props.steps];
    [next[index], next[target]] = [next[target], next[index]];
    update(next);
  };

  return (
    <div className="playlist-sequence">
      {props.steps.length === 0 && (
        <div className="playlist-sequence__empty">
          {t("playlist.settings.sequence.empty")}
        </div>
      )}

      {props.steps.map((step, index) => {
        // index key is intentional: the same drawer may legitimately repeat in a sequence,
        // so playlistId is not unique; rows are fully prop-controlled.
        return (
        <div className="playlist-sequence__row" key={index}>
          <span className="playlist-sequence__pos">{index + 1}.</span>
          <Select
            name={`step-${index}`}
            placeholder={t("playlist.settings.sequence.step")}
            options={options}
            value={{ label: labelFor(step.playlistId), value: step.playlistId }}
            onChange={(name, option) => onChangeStep(index, name, option)}
          />
          <button
            type="button"
            className="playlist-sequence__btn"
            title={t("playlist.settings.sequence.up")}
            disabled={index === 0}
            onClick={() => move(index, -1)}
          >
            <Icon.IonIcons5.IoArrowUp />
          </button>
          <button
            type="button"
            className="playlist-sequence__btn"
            title={t("playlist.settings.sequence.down")}
            disabled={index === props.steps.length - 1}
            onClick={() => move(index, 1)}
          >
            <Icon.IonIcons5.IoArrowDown />
          </button>
          <button
            type="button"
            className="playlist-sequence__btn playlist-sequence__btn--delete"
            title={t("playlist.settings.sequence.delete")}
            onClick={() => onDelete(index)}
          >
            <Icon.IonIcons5.IoTrashBinSharp />
          </button>
        </div>
        );
      })}

      <button
        type="button"
        className="playlist-sequence__add"
        title={t("playlist.settings.sequence.add")}
        aria-label={t("playlist.settings.sequence.add")}
        onClick={onAdd}
        disabled={props.playlists.length === 0}
      >
        + {t("playlist.settings.sequence.add")}
      </button>
    </div>
  );
};

export default PlaylistSequence;
